import { ReactNode } from 'react';
import type { UseReactTableStateResult } from '@trukt/tables/useReactTableStateProps';
import {
  GridTable,
  GridTableProps,
  useLinkedVerticalScroll,
  type GridTableColumnDef,
} from '@trukt/tables/GridTable';
import { OrderTableFields } from '../OrderListView.types';
import { Typography } from '@trukt/ui/Typography';
import { ListLoaderGuard } from '@trukt/ui/list-loader-guard';
import { PATH_NAMES } from '@components/pages/shared';
import { Button } from '@trukt/ui/Button';

const FIXED_TABLE_COLUMNS: GridTableColumnDef<OrderTableFields>[] = [
  {
    header: () => <Typography className="min-w-14">Order #</Typography>,
    id: 'number',
    accessorKey: 'number',
    enableSorting: true,
    meta: {
      width: 80,
    },
    cell: ({ row, getValue }) => (
      <div className="flex justify-center">
        <Button
          variant="link"
          typographyVariant="h5"
          to={PATH_NAMES.order.view(row.original.guid) as '/orders/$orderGuid'}
        >
          {getValue() as string}
        </Button>
      </div>
    ),
  },
];

interface OrderListContentProps extends Pick<
  GridTableProps<OrderTableFields>,
  'data' | 'isError' | 'isLoading' | 'errorMessage' | 'columns'
> {
  children?: ReactNode;
  tableProps: UseReactTableStateResult;
  isFetching: boolean | undefined;
}
export const OrderListContent = ({
  children,
  data,
  columns,
  isLoading,
  isError,
  errorMessage,
  tableProps,
  isFetching,
}: OrderListContentProps) => {
  const linkedVerticalScroll = useLinkedVerticalScroll();
  return (
    <>
      <div
        className="relative grid h-[calc(100%_-_34px)] grid-rows-1 gap-4 pb-4"
        style={{ gridTemplateColumns: 'minmax(0, 115px) minmax(0, 1fr)' }}
      >
        <ListLoaderGuard isLoading={isFetching} />
        <GridTable
          columns={FIXED_TABLE_COLUMNS}
          data={data}
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
          hideBodyScrollbar
          tableRef={linkedVerticalScroll.pinnedTableRef}
          onBodyScroll={linkedVerticalScroll.handlePinnedTableScroll}
          {...tableProps}
        />
        <GridTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
          tableRef={linkedVerticalScroll.scrollableTableRef}
          onBodyScroll={linkedVerticalScroll.handleScrollableTableScroll}
          {...tableProps}
        />
      </div>
      {children}
    </>
  );
};
